import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { DrugDetailComponent } from './drug-detail/drug-detail.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { debounceTime, distinctUntilChanged, finalize, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { PaginationResult } from 'src/app/models/common.response';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DrugService } from './drug.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { DeleteDialog } from 'src/app/pages/dialogs/delete.component';
import { IDrug } from './idrug';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';


@Component({
  selector: 'app-drug-list',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule,
    DefaultValuePipe
  ],
  templateUrl: './drug-list.component.html',
  styleUrl: './drug-list.component.scss'
})
export class DrugListComponent implements OnInit {

  displayedColumns: string[] = [
    'drugName',
    'genericName',
    'drugCompanyName',
    'drugTypeName',
    'genericContent',
    'instructions',
    'action',
  ];
  dataSource = new MatTableDataSource<IDrug>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  pagination: PaginationResult;
  drugcount: number = 0;
  drugs$: Observable<IDrug[]>;
  drugdata: IDrug[];
  isLoading: boolean = false;
  isLoadData: boolean;
  searchdrug: FormControl = new FormControl();
  searchQuery: string;
  pageSizeOptions = PAGE_SIZE_OPTIONS;


  constructor(
    private _drugService: DrugService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private loaderService: LoaderService,
    private translationUtils: TranslationUtils
  ) { }

  ngOnInit(): void {
    this._drugService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.drugcount = this.pagination.rowCount;
        }
      });

    this._drugService.drugs$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.drugdata = data;
        this.dataSource = new MatTableDataSource(this.drugdata); // Correct initialization
        this.dataSource.paginator = this.paginator;
      });

    this.searchdrug.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        return this._drugService.getAllDrugs({
          pageNumber: 0,
          pageSize: this.pagination.pageSize,
          isDescending: false,
          searchValue: query || ''
        }).pipe(
          finalize(() => (this.isLoading = false))
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.drugDetails;
        this.drugcount = response.result.pagination.rowCount;
      }
    });

  }

  onAddDrug() {
    this.dialog.open(DrugDetailComponent, {
      width: '90vh',
      height: 'auto',
      disableClose: true,
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshDrug();
      }
    });
  }

  onUpdateDrug(drugdetail: IDrug) {
    this.dialog.open(DrugDetailComponent, {
      width: '600px',
      height: 'auto',
      disableClose: true,
      data:
      {
        drug: drugdetail,
        isEdit: true
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshDrug();
      }
    });
  }

  refreshDrug() {
    const globalSearchValue = this.searchdrug?.value?.trim();
    const requestPayload: any = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false
    };

    if (globalSearchValue) {
      requestPayload.SearchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    this._drugService.getAllDrugs(requestPayload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        if (response && response.result) {
          const drug = response.result.drugDetails;
          this.drugdata = drug;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(drug);
          } else {
            this.dataSource.data = drug;
          }
          this.dataSource.paginator = this.paginator;
          this.drugcount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });

  }

  onDeleteDrug(id: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.DRUG') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._drugService.deleteDrug(id).subscribe({
          next: (response) => {
            if (response.isSuccess) {
              this.refreshDrug();
              this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.DRUG')));
            }
          },
          error: (error) => {
            if (error?.error?.errors) {
              const allErrors = Object.values(error.error.errors).flat();
              this.toastr.error(allErrors.join('\n'));
            } else {
              this.toastr.error('Something went wrong.');
            }
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

}
